import { ChangeDetectionStrategy, Component, DestroyRef, inject, input, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DatePipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { OverviewPaginationComponent } from '../../../components/overview-pagination/overview-pagination.component';
import { SectionTitleComponent } from '../../../components/section-title/section-title.component';
import { saveBlob } from '../../../shared/file-download';
import { KiAuditEintrag } from './ki-audit.model';
import { KiAuditService } from './ki-audit.service';

const SEITENGROESSE = 50;
const KEIN_WERT = '—';
const TOKENS = new Intl.NumberFormat('de-DE');
const SEKUNDEN = new Intl.NumberFormat('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

@Component({
  selector: 'app-ki-audit-log',
  imports: [
    DatePipe,
    MatButtonModule,
    MatProgressSpinnerModule,
    OverviewPaginationComponent,
    SectionTitleComponent,
    TranslatePipe,
  ],
  templateUrl: './ki-audit-log.component.html',
  styleUrl: './ki-audit-log.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KiAuditLogComponent implements OnInit {
  private readonly service = inject(KiAuditService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private ladeAbo?: Subscription;

  readonly inhalteGespeichert = input<boolean>(false);

  protected readonly keinWert = KEIN_WERT;
  protected readonly seitengroesse = SEITENGROESSE;
  protected readonly seitengroessen = [SEITENGROESSE] as const;
  protected readonly eintraege = signal<KiAuditEintrag[]>([]);
  protected readonly gesamt = signal(0);
  protected readonly seitenIndex = signal(0);
  protected readonly laedt = signal(true);
  protected readonly ladeFehler = signal(false);
  protected readonly exportLaeuft = signal(false);

  ngOnInit(): void {
    this.laden();
  }

  protected seiteWechseln(index: number): void {
    this.seitenIndex.set(index);
    this.laden();
  }

  protected laden(): void {
    this.ladeAbo?.unsubscribe();
    this.laedt.set(true);
    this.ladeFehler.set(false);
    this.ladeAbo = this.service
      .seite(this.seitenIndex(), SEITENGROESSE)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (seite) => {
          this.eintraege.set(seite.content);
          this.gesamt.set(seite.totalElements);
          this.laedt.set(false);
        },
        error: () => {
          this.eintraege.set([]);
          this.ladeFehler.set(true);
          this.laedt.set(false);
        },
      });
  }

  protected exportieren(): void {
    this.exportLaeuft.set(true);
    this.service
      .exportieren()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (datei) => {
          saveBlob(datei.blob, datei.dateiname);
          this.exportLaeuft.set(false);
        },
        error: () => {
          this.exportLaeuft.set(false);
          this.snackBar.open(this.translate.instant('ki.audit.exportFehler'), undefined, {
            duration: 3000,
            verticalPosition: 'top',
            panelClass: 'snackbar-error',
          });
        },
      });
  }

  protected tokens(anzahl: number): string {
    return anzahl === 0 ? KEIN_WERT : TOKENS.format(anzahl);
  }

  protected dauer(dauerMs: number): string {
    return `${SEKUNDEN.format(dauerMs / 1000)} s`;
  }
}
